import { CATEGORY_LABELS_MN } from './labels';
import { TopicCategory } from './types/enums';

/* ------------------------------------------------------------------ *
 * Roadmap
 * ------------------------------------------------------------------ */

export interface TopicSeed {
  slug: string;
  name: string;
  category: TopicCategory;
}

/**
 * The fixed roadmap. Order within a category is the intended learning order.
 *
 * Topic names stay in English on purpose: they are the established algorithm
 * theory terms the user reads in editorials and papers, and translating them
 * would make the roadmap harder to match against outside material.
 */
export const TOPIC_TREE: TopicSeed[] = [
  // Beginner
  { slug: 'complexity-analysis', name: 'Complexity Analysis', category: TopicCategory.BEGINNER },
  { slug: 'stl', name: 'STL', category: TopicCategory.BEGINNER },
  { slug: 'basic-math', name: 'Basic Math', category: TopicCategory.BEGINNER },
  { slug: 'sorting', name: 'Sorting', category: TopicCategory.BEGINNER },
  { slug: 'searching', name: 'Searching', category: TopicCategory.BEGINNER },

  // Intermediate
  { slug: 'binary-search', name: 'Binary Search', category: TopicCategory.INTERMEDIATE },
  { slug: 'two-pointers', name: 'Two Pointers', category: TopicCategory.INTERMEDIATE },
  { slug: 'sliding-window', name: 'Sliding Window', category: TopicCategory.INTERMEDIATE },
  { slug: 'prefix-sum', name: 'Prefix Sum', category: TopicCategory.INTERMEDIATE },
  { slug: 'greedy', name: 'Greedy', category: TopicCategory.INTERMEDIATE },
  { slug: 'recursion', name: 'Recursion', category: TopicCategory.INTERMEDIATE },
  { slug: 'backtracking', name: 'Backtracking', category: TopicCategory.INTERMEDIATE },

  // Advanced
  { slug: 'dynamic-programming', name: 'Dynamic Programming', category: TopicCategory.ADVANCED },
  { slug: 'graph-algorithms', name: 'Graph Algorithms', category: TopicCategory.ADVANCED },
  { slug: 'shortest-path', name: 'Shortest Path', category: TopicCategory.ADVANCED },
  { slug: 'mst', name: 'MST', category: TopicCategory.ADVANCED },
  { slug: 'tree-algorithms', name: 'Tree Algorithms', category: TopicCategory.ADVANCED },
  { slug: 'segment-tree', name: 'Segment Tree', category: TopicCategory.ADVANCED },
  { slug: 'fenwick-tree', name: 'Fenwick Tree', category: TopicCategory.ADVANCED },
  { slug: 'dsu', name: 'DSU', category: TopicCategory.ADVANCED },
  { slug: 'string-algorithms', name: 'String Algorithms', category: TopicCategory.ADVANCED },

  // Expert
  {
    slug: 'heavy-light-decomposition',
    name: 'Heavy-Light Decomposition',
    category: TopicCategory.EXPERT,
  },
  {
    slug: 'centroid-decomposition',
    name: 'Centroid Decomposition',
    category: TopicCategory.EXPERT,
  },
  {
    slug: 'persistent-data-structures',
    name: 'Persistent Data Structures',
    category: TopicCategory.EXPERT,
  },
  { slug: 'network-flow', name: 'Network Flow', category: TopicCategory.EXPERT },
  {
    slug: 'advanced-dp-optimization',
    name: 'Advanced DP Optimization',
    category: TopicCategory.EXPERT,
  },
  {
    slug: 'computational-geometry',
    name: 'Computational Geometry',
    category: TopicCategory.EXPERT,
  },
  { slug: 'game-theory', name: 'Game Theory', category: TopicCategory.EXPERT },
];

export const CATEGORY_LABELS: Record<TopicCategory, string> = CATEGORY_LABELS_MN;

export const CATEGORY_ORDER: TopicCategory[] = [
  TopicCategory.BEGINNER,
  TopicCategory.INTERMEDIATE,
  TopicCategory.ADVANCED,
  TopicCategory.EXPERT,
];

/* ------------------------------------------------------------------ *
 * Achievements
 * ------------------------------------------------------------------ */

/** How an achievement is evaluated. Kept declarative so the checker is one switch. */
export type AchievementRule =
  | { kind: 'problemsSolved'; threshold: number }
  | { kind: 'streakDays'; threshold: number }
  | { kind: 'topicsMastered'; threshold: number }
  | { kind: 'categoryMastered'; category: TopicCategory }
  | { kind: 'expertProblemSolved' };

export interface AchievementSeed {
  id: string;
  name: string;
  description: string;
  icon: string;
  sortOrder: number;
  rule: AchievementRule;
}

export const ACHIEVEMENTS: AchievementSeed[] = [
  {
    id: 'first-solve',
    name: 'Анхны бодолт',
    description: 'Хамгийн анхны бодсон бодлогоо бүртгэ.',
    icon: '🌱',
    sortOrder: 10,
    rule: { kind: 'problemsSolved', threshold: 1 },
  },
  {
    id: 'problems-10',
    name: '10 бодлого',
    description: 'Ялгаатай 10 бодлого бод.',
    icon: '🔟',
    sortOrder: 20,
    rule: { kind: 'problemsSolved', threshold: 10 },
  },
  {
    id: 'problems-50',
    name: '50 бодлого',
    description: 'Ялгаатай 50 бодлого бод.',
    icon: '⚡',
    sortOrder: 30,
    rule: { kind: 'problemsSolved', threshold: 50 },
  },
  {
    id: 'problems-100',
    name: '100 бодлого',
    description: 'Ялгаатай 100 бодлого бод.',
    icon: '💯',
    sortOrder: 40,
    rule: { kind: 'problemsSolved', threshold: 100 },
  },
  {
    id: 'problems-500',
    name: '500 бодлого',
    description: 'Ялгаатай 500 бодлого бод.',
    icon: '🏔️',
    sortOrder: 50,
    rule: { kind: 'problemsSolved', threshold: 500 },
  },
  {
    id: 'streak-7',
    name: '7 хоногийн цуваа',
    description: 'Дараалсан долоо хоног идэвхтэй бай.',
    icon: '🔥',
    sortOrder: 60,
    rule: { kind: 'streakDays', threshold: 7 },
  },
  {
    id: 'streak-30',
    name: '30 хоногийн цуваа',
    description: 'Дараалсан гучин хоног идэвхтэй бай.',
    icon: '🌋',
    sortOrder: 70,
    rule: { kind: 'streakDays', threshold: 30 },
  },
  {
    id: 'streak-100',
    name: '100 хоногийн цуваа',
    description: 'Дараалсан зуун хоног идэвхтэй бай.',
    icon: '☄️',
    sortOrder: 80,
    rule: { kind: 'streakDays', threshold: 100 },
  },
  {
    id: 'first-mastered-topic',
    name: 'Анхны эзэмшсэн сэдэв',
    description: 'Roadmap-ын аль нэг сэдвийг бүрэн эзэмш.',
    icon: '🎓',
    sortOrder: 90,
    rule: { kind: 'topicsMastered', threshold: 1 },
  },
  {
    id: 'beginner-complete',
    name: 'Анхан шат дуусгасан',
    description: 'Анхан шатны бүх сэдвийг эзэмш.',
    icon: '🧭',
    sortOrder: 100,
    rule: { kind: 'categoryMastered', category: TopicCategory.BEGINNER },
  },
  {
    id: 'first-expert-problem',
    name: 'Анхны онц хүнд бодлого',
    description: 'Онц хүнд гэж үнэлсэн бодлого бод.',
    icon: '🐉',
    sortOrder: 110,
    rule: { kind: 'expertProblemSolved' },
  },
];

/* ------------------------------------------------------------------ *
 * Activity thresholds
 * ------------------------------------------------------------------ */

/** A day counts as active with ≥ 1 solve or ≥ this many minutes logged. */
export const ACTIVE_DAY_MINUTES = 30;

/** Window used by the "weak topic" definition. */
export const WEAK_TOPIC_WINDOW_DAYS = 30;

/** A topic is weak at or below this confidence, strong at or above STRONG. */
export const WEAK_CONFIDENCE_MAX = 2;
export const STRONG_CONFIDENCE_MIN = 4;
export const STRONG_TOPIC_MIN_PROBLEMS = 10;

export const SUGGESTED_DAILY_TASKS = [
  '2 ширхэг Dynamic Programming бодлого бодох',
  'Graph Algorithms давтах',
  'Editorial унших',
  'Өнөөдрийн сэдвээр тэмдэглэл бичих',
  'Нэг бодлогыг цээжээр дахин бодох',
];
